import React from 'react';
import { Flex, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-thin-svg-icons';
import {
	Currencies,
	PnlItemTypes,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import { formatPercentage } from '@shared/utils/formatPercentage';
import TooltipIcon from '@client/components/TooltipIcon';
import Button from '@client/components/Button';
import styles from '@client/screens/fixtures/shared/styles/SummaryCard.module.css';
import { TablePnlEntry } from '../../VcPnl/VcPnl';

export const numberField = (v: number) => (v == null ? ' ' : formatAndRoundNumber(v, 2));

export const shouldInvert = (item: PnlItemTypes, tcPnl?: boolean): boolean => {
	return !!tcPnl && [
		PnlItemTypes.ADDRESS_COMMISSIONS,
		PnlItemTypes.BROKER_COMMISSIONS,
		PnlItemTypes.CAPEX,
		PnlItemTypes.OPEX,
		PnlItemTypes.TC_IN_COSTS,
	].includes(item);
};

const getPnlTableColumns = (
	currency: Values<typeof Currencies>,
	columnsToHide: ('actual' | 'estimated' | 'invoiced' | 'variance')[] = [],
	tcPnl?: boolean,
	actualOrExpected?: boolean,
): any => {
	const showActual = !columnsToHide.includes('actual');
	const showEstimated = !columnsToHide.includes('estimated');
	const showInvoiced = !columnsToHide.includes('invoiced');
	const showVariance = !columnsToHide.includes('variance');

	const currencyField = (v: number) => (v == null ? ' ' : formatCurrency(v, currency, { forceDecimals: true, maximumFractionDigits: 2 }));

	const formatWithStyles = (
		value: React.ReactNode,
		entry: TablePnlEntry,
	): React.ReactNode => {
		let formattedValue: React.ReactNode = value;

		if (entry.bold) {
			formattedValue = (<b>{formattedValue}</b>);
		}

		if (entry.highlight) {
			formattedValue = (<div className={styles.highlightPnlTable}>{formattedValue}</div>);
		}

		return formattedValue;
	};

	return [
		{
			title: 'Description',
			dataIndex: 'item',
			key: 'item',
			render: (item: string, entry: TablePnlEntry) => {
				if (typeof entry.onClick === 'function') {
					return (
						<Flex gap={10} align="center">
							{(entry.bold ? (<b>{item}</b>) : (<p>{item}</p>))}
							<Button
								data-testid={entry.actionTestId ?? 'pnl-button'}
								onClick={entry.onClick}
								icon={(<FontAwesomeIcon icon={entry.icon ?? faEdit} />)}
							/>
						</Flex>
					);
				}

				if (entry.icon) {
					const icon = (
						<span data-testid="pnl-warning-icon">
							<FontAwesomeIcon
								icon={entry.icon}
								color={entry.iconColor}
							/>
						</span>
					);

					return (
						<Flex gap={10} align="center">
							{(entry.bold ? (<b>{item}</b>) : (<p>{item}</p>))}
							{entry.iconTooltip ? (
								<Tooltip
									title={entry.iconTooltip}
									trigger={['hover', 'click']}
								>
									{icon}
								</Tooltip>
							) : icon}
						</Flex>
					);
				}

				return formatWithStyles(item, entry);
			},
		},
		...(showEstimated ? [
			{
				title: 'Estimated',
				dataIndex: 'estimated',
				key: 'estimated',
				render: (v: number, entry: TablePnlEntry) => {
					const value =
						shouldInvert(entry.item as PnlItemTypes, tcPnl) ?
							currencyField(v * -1) : entry.number != null ?
								numberField(v) :
								currencyField(v);

					return formatWithStyles(value, entry);
				},
				width: 150,
				align: 'right' as const,
			},
		] : []),
		...(showActual ? [
			{
				title: tcPnl && actualOrExpected ? (
					<div>
						Actual
						<TooltipIcon>
							This is a forecast of how the contract is expected to finish.
							This is calculated by taking all invoiced numbers so far,
							and using estimated numbers for the remainder of the contract
						</TooltipIcon>
					</div>
				) : 'Actual',
				dataIndex: 'actual',
				key: 'actual',
				render: (v: number, entry: TablePnlEntry) => {
					const value = shouldInvert(entry.item as PnlItemTypes, tcPnl) ?
						currencyField(v * -1) :
						entry.number != null ?
							numberField(v) :
							currencyField(v);

					return formatWithStyles(value, entry);
				},
				width: 150,
				align: 'right' as const,
			},
		] : []),
		...((tcPnl && showInvoiced) ? [{
			title: 'Invoiced',
			dataIndex: 'invoiced',
			key: 'invoiced',
			render: (v: number, entry: TablePnlEntry) => {
				const value =
					shouldInvert(entry.item as PnlItemTypes, tcPnl) ?
						currencyField(v * -1) :
						entry.number != null ?
							numberField(v) :
							currencyField(v);

				return formatWithStyles(value, entry);
			},
			width: 150,
			align: 'right' as const,
		}] : []),
		...(showVariance ? [
			{
				title: tcPnl ?
					`Var (${actualOrExpected ? 'Exp' : 'Act'} - Est)` :
					'Variance',
				key: 'varAmount',
				render: (entry: TablePnlEntry, _: any) => {
					const variance = entry.actual - entry.estimated;

					const value = entry.number != null ?
						numberField(variance) :
						currencyField(variance);

					return formatWithStyles(value, entry);
				},
				align: 'right' as const,
				width: 150,
			},
			{
				title: '%',
				key: 'varPercentage',
				render: (entry: TablePnlEntry, _: any) => {
					const variance = entry.actual - entry.estimated;

					const percentage = entry.estimated === 0 ?
						0 :
						(variance / entry.estimated) * 100;

					const value = formatPercentage(percentage, 2);

					return formatWithStyles(value, entry);
				},
				align: 'right' as const,
				width: 80,
			},
		] : []),
	];
};

export default getPnlTableColumns;
